import { useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel, Tag } from "@/components/common";
import { Flask, Play, Spinner, CheckCircle, XCircle } from "@phosphor-icons/react";

const COMPONENTS = [
  { id: "image", label: "Generate 1 image" },
  { id: "video", label: "Generate 3-second video" },
  { id: "voice", label: "Generate 1 sentence voice" },
  { id: "lipsync", label: "Lip-sync 3-second clip" },
  { id: "music", label: "Generate 10-second music" },
  { id: "stt", label: "Transcribe test audio" },
];

export default function LocalTestLab() {
  const [running, setRunning] = useState({});
  const [results, setResults] = useState({});

  const run = async (id) => {
    setRunning((r) => ({ ...r, [id]: true }));
    try {
      const { data } = await api.post(`/local/test/${id}`);
      setResults((r) => ({ ...r, [id]: data }));
    } catch {
      setResults((r) => ({ ...r, [id]: { ok: false, status: "ERROR", message: "Request failed" } }));
    } finally {
      setRunning((r) => ({ ...r, [id]: false }));
    }
  };

  return (
    <div>
      <PageHeader icon={Flask} title="Local AI Test Lab"
        subtitle="Test each open-source engine independently. Results are real — if no self-hosted endpoint is attached, you'll see the honest blocking status, never a fake output." />

      <div className="grid sm:grid-cols-2 gap-5">
        {COMPONENTS.map((c) => {
          const res = results[c.id];
          const busy = running[c.id];
          return (
            <Panel key={c.id} data-testid={`lab-${c.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-display font-bold text-white">{c.label}</span>
                <button data-testid={`lab-run-${c.id}`} onClick={() => run(c.id)} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full bg-magic px-4 py-1.5 text-sm font-semibold text-black disabled:opacity-60">
                  {busy ? <Spinner size={14} className="animate-spin" /> : <Play size={14} weight="fill" />} Run
                </button>
              </div>
              {res && (
                <div className="rounded-xl border border-white/8 bg-black/20 p-3 text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    {res.ok ? <CheckCircle size={16} weight="fill" className="text-[#00FF9D]" /> : <XCircle size={16} weight="fill" className="text-[#FFB800]" />}
                    <span className="font-mono text-xs" style={{ color: res.ok ? "#00FF9D" : "#FFB800" }}>{res.status}</span>
                    {typeof res.time_ms === "number" && <Tag className="text-[10px] ml-auto">{res.time_ms} ms</Tag>}
                  </div>
                  <p className="text-zinc-300">{res.message}</p>
                  {res.hardware && (
                    <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-zinc-500">
                      <span>GPU: {res.hardware.gpu_detected ? res.hardware.gpu.name : "none"}</span>
                      <span>VRAM: {res.hardware.vram_gb} GB</span>
                      <span>RAM: {res.hardware.ram_gb} GB</span>
                    </div>
                  )}
                  {res.output && <pre className="mt-2 text-[11px] text-zinc-400 whitespace-pre-wrap break-all">{res.output}</pre>}
                </div>
              )}
              {!res && !busy && <p className="text-xs text-zinc-500">Not tested yet.</p>}
            </Panel>
          );
        })}
      </div>
    </div>
  );
}
